import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';

const STAGE_COLORS = { applied: 'bg-gray-100 text-gray-700', screening: 'bg-yellow-50 text-yellow-700', interview: 'bg-blue-50 text-blue-700', offer: 'bg-purple-50 text-purple-700', hired: 'bg-green-50 text-green-700', rejected: 'bg-red-50 text-red-700' };

export default function ApplicantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [applicant, setApplicant] = useState(null);
  const [stages, setStages] = useState([]);
  const [notes, setNotes] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [scorecards, setScorecards] = useState([]);
  const [activities, setActivities] = useState([]);
  const [tab, setTab] = useState('notes');
  const [noteText, setNoteText] = useState('');
  const [showInterview, setShowInterview] = useState(false);
  const [ivForm, setIvForm] = useState({ interviewer: '', scheduledAt: '', durationMinutes: 60, location: '', type: 'in_person', notes: '' });
  const [completingInterview, setCompletingInterview] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [showScorecard, setShowScorecard] = useState(false);
  const [scForm, setScForm] = useState({ interviewer: '', overallRating: 3, recommendation: 'yes', comments: '', ratings: [{ criteria: 'Technical Skills', rating: 3 }, { criteria: 'Communication', rating: 3 }, { criteria: 'Culture Fit', rating: 3 }] });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [offer, setOffer] = useState(null);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerForm, setOfferForm] = useState({ jobTitle: '', salary: '', startDate: '', content: '', status: 'draft' });
  const [attachments, setAttachments] = useState([]);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [templates, setTemplates] = useState([]);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [emailTemplateId, setEmailTemplateId] = useState('');
  const [rejectionModal, setRejectionModal] = useState(null);

  const load = () => {
    api(`/ats/applicants/${id}`).then(r => r.ok ? r.json() : null).then(setApplicant).catch(() => {});
    api(`/ats/applicants/${id}/notes`).then(r => r.ok ? r.json() : []).then(setNotes).catch(() => {});
    api(`/ats/applicants/${id}/interviews`).then(r => r.ok ? r.json() : []).then(setInterviews).catch(() => {});
    api(`/ats/applicants/${id}/scorecards`).then(r => r.ok ? r.json() : []).then(setScorecards).catch(() => {});
    api(`/ats/applicants/${id}/activities`).then(r => r.ok ? r.json() : []).then(setActivities).catch(() => {});
    api(`/ats/applicants/${id}/offer-letter`).then(r => r.ok ? r.json() : null).then(setOffer).catch(() => {});
    api(`/ats/applicants/${id}/attachments`).then(r => r.ok ? r.json() : []).then(setAttachments).catch(() => {});
    api(`/ats/applicants/${id}/tags`).then(r => r.ok ? r.json() : []).then(setTags).catch(() => {});
    api('/ats/pipeline-stages').then(r => r.ok ? r.json() : []).then(setStages).catch(() => {});
    api('/ats/email-templates').then(r => r.ok ? r.json() : []).then(setTemplates).catch(() => {});
  };
  useEffect(() => { load(); }, [id]);

  const moveStage = async (stage) => {
    if (stage === 'rejected') {
      setRejectionModal(stage);
      return;
    }
    await api(`/ats/applicants/${id}`, { method: 'PUT', body: JSON.stringify({ stage }) }); load();
  };
  const addNote = async (e) => { e.preventDefault(); if (!noteText.trim()) return; await api(`/ats/applicants/${id}/notes`, { method: 'POST', body: JSON.stringify({ text: noteText }) }); setNoteText(''); load(); };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    await api(`/ats/applicants/${id}/resume`, { method: 'POST', body: fd }); load();
  };
  const viewResume = async () => { const r = await api(`/ats/applicants/${id}/resume`); if (r.ok) { const d = await r.json(); window.open(d.url, '_blank'); } };

  const scheduleInterview = async (e) => {
    e.preventDefault();
    await api(`/ats/applicants/${id}/interviews`, { method: 'POST', body: JSON.stringify({ ...ivForm, applicantId: id, scheduledAt: ivForm.scheduledAt ? new Date(ivForm.scheduledAt).toISOString() : null }) });
    setShowInterview(false); setIvForm({ interviewer: '', scheduledAt: '', durationMinutes: 60, location: '', type: 'in_person', notes: '' }); load();
  };

  const submitScorecard = async (e) => {
    e.preventDefault();
    await api(`/ats/applicants/${id}/scorecards`, { method: 'POST', body: JSON.stringify({ ...scForm, applicantId: id }) });
    setShowScorecard(false); load();
  };

  const convertToEmployee = async () => {
    const r = await api(`/ats/applicants/${id}/convert-to-employee`, { method: 'POST' });
    if (r.ok) { const d = await r.json(); navigate(`/hcm/employees/${d.employeeId}`); }
  };

  if (!applicant) return <Layout><div className="text-center py-12 text-gray-500">Loading...</div></Layout>;

  const input = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600";

  return (
    <Layout>
      <div className="mb-6"><Link to="/ats/applicants" className="text-teal-600 hover:text-teal-800 text-sm">← Back to Applicants</Link></div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{applicant.firstName} {applicant.lastName}</h2>
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  {applicant.email && <p>{applicant.email}</p>}
                  {applicant.phone && <p>{applicant.phone}</p>}
                  {applicant.jobTitle && <p>Applied for: <span className="font-medium text-gray-900">{applicant.jobTitle}</span></p>}
                  {applicant.source && <p>Source: {applicant.source}</p>}
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {tags.map(t => (
                      <span key={t.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-700 rounded text-xs">
                        {t.tag}
                        <button onClick={async () => { await api(`/ats/applicant-tags/${t.id}`, {method: 'DELETE'}); load(); }} className="hover:text-red-500">&times;</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${STAGE_COLORS[applicant.stage] || 'bg-gray-100 text-gray-600'}`}>{applicant.stage}</span>
                {applicant.stage === 'hired' && <button onClick={convertToEmployee} className="bg-green-600 text-white px-3 py-1 rounded text-xs font-medium hover:bg-green-700">Convert to Employee</button>}
                <button onClick={async () => { await api(`/ats/applicants/${id}/archive`, { method: applicant.archived ? 'DELETE' : 'POST' }); load(); }}
                  className={`px-3 py-1 rounded text-xs font-medium ${applicant.archived ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {applicant.archived ? 'Unarchive' : 'Archive'}
                </button>
                <button onClick={() => setShowDeleteConfirm(true)} className="px-3 py-1 rounded text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100">Delete</button>
                <button onClick={() => setShowSendEmail(true)} className="px-3 py-1 rounded text-xs font-medium bg-blue-50 text-blue-600 hover:bg-blue-100">Send Email</button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline</h3>
            <div className="flex flex-wrap gap-2">
              {stages.map(stage => {
                const name = stage.name.toLowerCase();
                return (
                  <button key={stage.id || stage.name} onClick={() => moveStage(name)} disabled={applicant.stage === name}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition ${applicant.stage === name ? (STAGE_COLORS[name] || 'bg-gray-100 text-gray-600') + ' ring-2 ring-offset-1 ring-teal-600' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>{stage.name}</button>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="border-b border-gray-200">
              <nav className="flex gap-6 px-6" aria-label="Tabs">
                {[['notes','Notes'],['interviews','Interviews'],['scorecards','Scorecards'],['offer','Offer Letter'],['attachments','Attachments'],['activity','Activity']].map(([key, label]) => (
                  <button key={key} onClick={() => setTab(key)} className={`py-3 text-sm font-medium border-b-2 transition-colors ${tab === key ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{label}</button>
                ))}
              </nav>
            </div>
            <div className="p-6">
              {tab === 'notes' && (
                <>
                  <form onSubmit={addNote} className="flex gap-2 mb-4">
                    <input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..." className={`flex-1 ${input}`} />
                    <button type="submit" className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium">Add</button>
                  </form>
                  <div className="space-y-3">
                    {notes.map(n => (<div key={n.id} className="p-3 bg-gray-50 rounded-lg"><p className="text-sm text-gray-900">{n.text}</p><p className="text-xs text-gray-500 mt-1">{n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}</p></div>))}
                    {notes.length === 0 && <p className="text-sm text-gray-500">No notes yet.</p>}
                  </div>
                </>
              )}

              {tab === 'interviews' && (
                <>
                  <button onClick={() => setShowInterview(true)} className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium mb-4">+ Schedule Interview</button>
                  {showInterview && (
                    <form onSubmit={scheduleInterview} className="p-4 border border-gray-200 rounded-lg mb-4 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div><label className="block text-xs font-medium text-gray-700 mb-1">Interviewer</label><input value={ivForm.interviewer} onChange={e => setIvForm({...ivForm, interviewer: e.target.value})} className={input} /></div>
                        <div><label className="block text-xs font-medium text-gray-700 mb-1">Date & Time *</label><input type="datetime-local" required value={ivForm.scheduledAt} onChange={e => setIvForm({...ivForm, scheduledAt: e.target.value})} className={input} /></div>
                        <div><label className="block text-xs font-medium text-gray-700 mb-1">Duration (min)</label><input type="number" value={ivForm.durationMinutes} onChange={e => setIvForm({...ivForm, durationMinutes: parseInt(e.target.value)})} className={input} /></div>
                        <div><label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                          <select value={ivForm.type} onChange={e => setIvForm({...ivForm, type: e.target.value})} className={input}>
                            <option value="in_person">In Person</option><option value="phone">Phone</option><option value="video">Video</option>
                          </select>
                        </div>
                        <div><label className="block text-xs font-medium text-gray-700 mb-1">Location</label><input value={ivForm.location} onChange={e => setIvForm({...ivForm, location: e.target.value})} className={input} /></div>
                      </div>
                      <div className="flex gap-2">
                        <button type="submit" className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm">Schedule</button>
                        <button type="button" onClick={() => setShowInterview(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm">Cancel</button>
                      </div>
                    </form>
                  )}
                  <div className="space-y-3">
                    {interviews.map(iv => (
                      <div key={iv.id} className="p-3 border border-gray-200 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{iv.scheduledAt ? new Date(iv.scheduledAt).toLocaleString() : 'TBD'}</p>
                            <p className="text-xs text-gray-500">{iv.interviewer || 'No interviewer'} · {iv.type || ''} · {iv.durationMinutes}min{iv.location ? ` · ${iv.location}` : ''}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${iv.status === 'completed' ? 'bg-green-50 text-green-700' : iv.status === 'cancelled' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>{iv.status}</span>
                            {iv.status === 'scheduled' && <button onClick={() => { setCompletingInterview(iv.id); setFeedbackText(''); }} className="text-xs text-teal-600 hover:text-teal-800 font-medium">Complete</button>}
                          </div>
                        </div>
                        {iv.feedback && <p className="text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded"><span className="text-xs font-medium text-gray-500">Feedback:</span> {iv.feedback}</p>}
                        {completingInterview === iv.id && (
                          <form onSubmit={async (e) => { e.preventDefault(); await api(`/ats/interviews/${iv.id}/complete`, { method: 'POST', body: JSON.stringify({ feedback: feedbackText }) }); setCompletingInterview(null); load(); }} className="mt-3 p-3 border border-teal-200 rounded-lg bg-teal-50 space-y-2">
                            <label className="block text-xs font-medium text-gray-700">Feedback *</label>
                            <textarea required rows={3} value={feedbackText} onChange={e => setFeedbackText(e.target.value)} className={input} placeholder="How did the interview go?" />
                            <div className="flex gap-2">
                              <button type="submit" className="bg-teal-600 text-white px-3 py-1.5 rounded-lg hover:bg-teal-700 text-sm">Mark Complete</button>
                              <button type="button" onClick={() => setCompletingInterview(null)} className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-sm">Cancel</button>
                            </div>
                          </form>
                        )}
                      </div>
                    ))}
                    {interviews.length === 0 && <p className="text-sm text-gray-500">No interviews scheduled.</p>}
                  </div>
                </>
              )}

              {tab === 'scorecards' && (
                <>
                  <button onClick={() => setShowScorecard(true)} className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium mb-4">+ Add Scorecard</button>
                  {showScorecard && (
                    <form onSubmit={submitScorecard} className="p-4 border border-gray-200 rounded-lg mb-4 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div><label className="block text-xs font-medium text-gray-700 mb-1">Interviewer</label><input value={scForm.interviewer} onChange={e => setScForm({...scForm, interviewer: e.target.value})} className={input} /></div>
                        <div><label className="block text-xs font-medium text-gray-700 mb-1">Overall Rating (1-5)</label><input type="number" min="1" max="5" value={scForm.overallRating} onChange={e => setScForm({...scForm, overallRating: parseInt(e.target.value)})} className={input} /></div>
                        <div><label className="block text-xs font-medium text-gray-700 mb-1">Recommendation</label>
                          <select value={scForm.recommendation} onChange={e => setScForm({...scForm, recommendation: e.target.value})} className={input}>
                            <option value="strong_yes">Strong Yes</option><option value="yes">Yes</option><option value="neutral">Neutral</option><option value="no">No</option><option value="strong_no">Strong No</option>
                          </select>
                        </div>
                      </div>
                      <div><label className="block text-xs font-medium text-gray-700 mb-1">Comments</label><textarea rows={2} value={scForm.comments} onChange={e => setScForm({...scForm, comments: e.target.value})} className={input} /></div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">Criteria Ratings</label>
                        {scForm.ratings.map((r, i) => (
                          <div key={i} className="flex gap-2 mb-2 items-center">
                            <input value={r.criteria} onChange={e => { const next = [...scForm.ratings]; next[i] = {...next[i], criteria: e.target.value}; setScForm({...scForm, ratings: next}); }} className={`flex-1 ${input}`} placeholder="Criteria" />
                            <input type="number" min="1" max="5" value={r.rating} onChange={e => { const next = [...scForm.ratings]; next[i] = {...next[i], rating: parseInt(e.target.value)}; setScForm({...scForm, ratings: next}); }} className={`w-16 ${input}`} />
                            <button type="button" onClick={() => setScForm({...scForm, ratings: scForm.ratings.filter((_, j) => j !== i)})} className="text-red-500 hover:text-red-700">&times;</button>
                          </div>
                        ))}
                        <button type="button" onClick={() => setScForm({...scForm, ratings: [...scForm.ratings, { criteria: '', rating: 3 }]})} className="text-xs text-teal-600 hover:text-teal-800">+ Add criteria</button>
                      </div>
                      <div className="flex gap-2">
                        <button type="submit" className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm">Submit</button>
                        <button type="button" onClick={() => setShowScorecard(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm">Cancel</button>
                      </div>
                    </form>
                  )}
                  <div className="space-y-3">
                    {scorecards.map(sc => (
                      <div key={sc.id} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-900">{sc.interviewer || 'Unknown'}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{'★'.repeat(sc.overallRating || 0)}{'☆'.repeat(5 - (sc.overallRating || 0))}</span>
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${sc.recommendation?.includes('yes') ? 'bg-green-50 text-green-700' : sc.recommendation?.includes('no') ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{sc.recommendation}</span>
                          </div>
                        </div>
                        {sc.comments && <p className="text-sm text-gray-600 mb-2">{sc.comments}</p>}
                        {sc.ratings?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {sc.ratings.map((r, i) => <span key={i} className="text-xs bg-gray-50 px-2 py-1 rounded">{r.criteria}: {r.rating}/5</span>)}
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-2">{sc.createdAt ? new Date(sc.createdAt).toLocaleString() : ''}</p>
                      </div>
                    ))}
                    {scorecards.length === 0 && <p className="text-sm text-gray-500">No scorecards yet.</p>}
                  </div>
                </>
              )}

              {tab === 'offer' && (
                <>
                  {offer && !showOfferForm ? (
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">{offer.jobTitle}</h4>
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium mt-1 ${offer.status === 'accepted' ? 'bg-green-50 text-green-700' : offer.status === 'declined' ? 'bg-red-50 text-red-700' : offer.status === 'sent' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{offer.status}</span>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => { setOfferForm({ jobTitle: offer.jobTitle || '', salary: offer.salary ?? '', startDate: offer.startDate || '', content: offer.content || '', status: offer.status || 'draft' }); setShowOfferForm(true); }} className="text-sm text-gray-600 hover:text-teal-600">Edit</button>
                          <button onClick={async () => {
                            const r = await api(`/ats/offer-letters/${offer.id}/pdf`);
                            if (r.ok) { const blob = await r.blob(); const url = URL.createObjectURL(blob); window.open(url, '_blank'); URL.revokeObjectURL(url); }
                          }} className="text-sm text-teal-600 hover:text-teal-800">Download PDF</button>
                          <button onClick={async () => { await api(`/ats/offer-letters/${offer.id}`, { method: 'DELETE' }); setOffer(null); load(); }} className="text-sm text-gray-600 hover:text-red-600">Delete</button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                        {offer.salary != null && <div><p className="text-gray-500">Salary</p><p className="font-medium text-gray-900">${Number(offer.salary).toLocaleString()}</p></div>}
                        {offer.startDate && <div><p className="text-gray-500">Start Date</p><p className="font-medium text-gray-900">{offer.startDate}</p></div>}
                      </div>
                      {offer.content && <div className="text-sm text-gray-700 whitespace-pre-wrap border-t pt-3">{offer.content}</div>}
                    </div>
                  ) : (
                    <>
                      {!showOfferForm && <button onClick={() => { setOfferForm({ jobTitle: applicant.jobTitle || '', salary: '', startDate: '', content: '', status: 'draft' }); setShowOfferForm(true); }} className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium mb-4">+ Create Offer Letter</button>}
                      {showOfferForm && (
                        <form onSubmit={async (e) => {
                          e.preventDefault();
                          const body = { ...offerForm, salary: offerForm.salary !== '' ? parseFloat(offerForm.salary) : null, startDate: offerForm.startDate || null };
                          if (offer) { await api(`/ats/offer-letters/${offer.id}`, { method: 'PUT', body: JSON.stringify(body) }); }
                          else { await api(`/ats/applicants/${id}/offer-letter`, { method: 'POST', body: JSON.stringify(body) }); }
                          setShowOfferForm(false); load();
                        }} className="p-4 border border-gray-200 rounded-lg space-y-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div><label className="block text-xs font-medium text-gray-700 mb-1">Job Title *</label><input required value={offerForm.jobTitle} onChange={e => setOfferForm({...offerForm, jobTitle: e.target.value})} className={input} /></div>
                            <div><label className="block text-xs font-medium text-gray-700 mb-1">Salary</label><input type="number" step="0.01" value={offerForm.salary} onChange={e => setOfferForm({...offerForm, salary: e.target.value})} className={input} /></div>
                            <div><label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label><input type="date" value={offerForm.startDate} onChange={e => setOfferForm({...offerForm, startDate: e.target.value})} className={input} /></div>
                            <div><label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                              <select value={offerForm.status} onChange={e => setOfferForm({...offerForm, status: e.target.value})} className={input}>
                                <option value="draft">Draft</option><option value="sent">Sent</option><option value="accepted">Accepted</option><option value="declined">Declined</option>
                              </select>
                            </div>
                          </div>
                          <div><label className="block text-xs font-medium text-gray-700 mb-1">Content</label><textarea rows={6} value={offerForm.content} onChange={e => setOfferForm({...offerForm, content: e.target.value})} className={input} placeholder="Offer letter body text..." /></div>
                          <div className="flex gap-2">
                            <button type="submit" className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm">{offer ? 'Save' : 'Create'}</button>
                            <button type="button" onClick={() => setShowOfferForm(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm">Cancel</button>
                          </div>
                        </form>
                      )}
                    </>
                  )}
                </>
              )}

              {tab === 'attachments' && (
                <>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const fd = new FormData(e.target);
                    await api(`/ats/applicants/${id}/attachments`, { method: 'POST', body: fd });
                    e.target.reset(); load();
                  }} className="flex flex-wrap gap-2 mb-4 items-end">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">File *</label>
                      <input type="file" name="file" required className="text-sm text-gray-600" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Label</label>
                      <input name="label" placeholder="e.g. Cover Letter" className={`w-48 ${input}`} />
                    </div>
                    <button type="submit" className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium">Upload</button>
                  </form>
                  <div className="space-y-2">
                    {attachments.map(att => (
                      <div key={att.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{att.fileName}</p>
                          <p className="text-xs text-gray-500">{att.label ? `${att.label} · ` : ''}{att.createdAt ? new Date(att.createdAt).toLocaleDateString() : ''}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button onClick={async () => { const r = await api(`/ats/applicant-attachments/${att.id}`); if (r.ok) { const d = await r.json(); window.open(d.url, '_blank'); } }} className="text-sm text-teal-600 hover:text-teal-800">Download</button>
                          <button onClick={async () => { await api(`/ats/applicant-attachments/${att.id}`, { method: 'DELETE' }); load(); }} className="text-sm text-gray-500 hover:text-red-600">Delete</button>
                        </div>
                      </div>
                    ))}
                    {attachments.length === 0 && <p className="text-sm text-gray-500">No attachments yet.</p>}
                  </div>
                </>
              )}

              {tab === 'activity' && (
                <div className="space-y-3">
                  {activities.map(a => (
                    <div key={a.id} className="flex gap-3 items-start">
                      <div className="w-2 h-2 rounded-full bg-teal-500 mt-1.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-900">{a.description}</p>
                        <p className="text-xs text-gray-500">{a.createdAt ? new Date(a.createdAt).toLocaleString() : ''}</p>
                      </div>
                    </div>
                  ))}
                  {activities.length === 0 && <p className="text-sm text-gray-500">No activity yet.</p>}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Resume</h3>
            {applicant.resumeFileName ? (
              <div className="space-y-2">
                <button onClick={viewResume} className="text-teal-600 hover:text-teal-800 text-sm font-medium">📎 {applicant.resumeFileName}</button>
                <div><label className="text-sm text-gray-500 hover:text-teal-600 cursor-pointer">Replace<input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={handleResumeUpload} /></label></div>
              </div>
            ) : (
              <label className="text-sm text-teal-600 hover:text-teal-800 cursor-pointer font-medium">+ Upload resume<input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={handleResumeUpload} /></label>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Details</h3>
            <div className="space-y-3 text-sm">
              <div><p className="text-gray-500">Applied</p><p className="text-gray-900 font-medium">{applicant.appliedAt ? new Date(applicant.appliedAt).toLocaleDateString() : '—'}</p></div>
              {applicant.source && <div><p className="text-gray-500">Source</p><p className="text-gray-900 font-medium">{applicant.source}</p></div>}
              {applicant.rejectionReason && <div><p className="text-gray-500">Rejection Reason</p><p className="text-gray-900 font-medium">{applicant.rejectionReason}</p></div>}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
            <div className="flex flex-wrap gap-1 mb-3">
              {tags.map(t => (
                <span key={t.id} className="inline-flex items-center gap-1 px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs">
                  {t.tag}
                  <button onClick={async () => { await api(`/ats/applicant-tags/${t.id}`, {method: 'DELETE'}); load(); }} className="hover:text-red-500">&times;</button>
                </span>
              ))}
            </div>
            <form onSubmit={async (e) => { e.preventDefault(); if (!newTag.trim()) return; await api(`/ats/applicants/${id}/tags`, {method: 'POST', body: JSON.stringify({tag: newTag.trim()})}); setNewTag(''); load(); }} className="flex gap-2">
              <input value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="Add tag..." className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500" />
              <button type="submit" className="text-xs text-teal-600 hover:text-teal-800 font-medium">Add</button>
            </form>
          </div>
        </div>
      </div>
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Applicant</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to permanently delete {applicant.firstName} {applicant.lastName}? This will remove all notes, interviews, scorecards, and attachments.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={async () => { await api(`/ats/applicants/${id}`, { method: 'DELETE' }); navigate('/ats/applicants'); }} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
      {showSendEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowSendEmail(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Send Email</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
              <select value={emailTemplateId} onChange={e => setEmailTemplateId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Select a template...</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowSendEmail(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button disabled={!emailTemplateId} onClick={async () => { await api(`/ats/applicants/${id}/send-email`, {method: 'POST', body: JSON.stringify({templateId: emailTemplateId})}); setShowSendEmail(false); setEmailTemplateId(''); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">Send</button>
            </div>
          </div>
        </div>
      )}
      {rejectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setRejectionModal(null)}>
          <form onClick={e => e.stopPropagation()} onSubmit={async (e) => {
            e.preventDefault();
            const reason = e.target.reason.value;
            await api(`/ats/applicants/${id}`, {method: 'PUT', body: JSON.stringify({stage: 'rejected', rejectionReason: reason || null})});
            setRejectionModal(null); load();
          }} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Reject Applicant</h3>
            <p className="text-gray-500 text-sm mb-4">Optionally provide a reason for rejecting {applicant.firstName} {applicant.lastName}.</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <select name="reason" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2">
                <option value="">No reason</option>
                <option value="Not qualified">Not qualified</option>
                <option value="Position filled">Position filled</option>
                <option value="Salary expectations">Salary expectations</option>
                <option value="Culture fit">Culture fit</option>
                <option value="Withdrew application">Withdrew application</option>
                <option value="No response">No response</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setRejectionModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Reject</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
